import { ConfigMap } from "store/platform-config/platformConfigSlice";

/** All platforms whose configs can be managed from this panel. */
export const PLATFORM_OPTIONS = [
  "ppd-app",
  "delivery-app",
  "wms-app",
  "fif-ppd-app",
  "wms-web",
] as const;

export type Platform = typeof PLATFORM_OPTIONS[number];

export const DEFAULT_VERSION = "default";

/** The value shapes a config key can hold. */
export type ConfigValueType = "string" | "integer" | "double" | "boolean" | "list" | "map";

export const CONFIG_VALUE_TYPES: ConfigValueType[] = [
  "string",
  "integer",
  "double",
  "boolean",
  "list",
  "map",
];

/**
 * One editable config row. `value` is kept in an editor-friendly shape:
 * - string:  string ("" is saved as null)
 * - integer/double: number | null
 * - boolean: boolean
 * - list:    string[] (tokens; numeric/boolean tokens are converted back on save)
 * - map:     string (JSON text, validated on save)
 */
export interface IConfigEntry {
  /** Stable row identity for React lists, independent of the (editable) key. */
  rowId: string;
  key: string;
  type: ConfigValueType;
  value: string | number | boolean | string[] | null;
}

let rowIdCounter = 0;
export const nextRowId = () => `config-row-${++rowIdCounter}`;

export const inferValueType = (value: unknown): ConfigValueType => {
  if (Array.isArray(value)) return "list";
  if (value !== null && typeof value === "object") return "map";
  if (typeof value === "boolean") return "boolean";
  if (typeof value === "number") return Number.isInteger(value) ? "integer" : "double";
  return "string"; // strings and nulls
};

export const defaultValueForType = (type: ConfigValueType): IConfigEntry["value"] => {
  switch (type) {
    case "boolean":
      return false;
    case "integer":
    case "double":
      return null;
    case "list":
      return [];
    case "map":
      return "{}";
    default:
      return "";
  }
};

const toEditorValue = (value: unknown, type: ConfigValueType): IConfigEntry["value"] => {
  switch (type) {
    case "list":
      return (value as unknown[]).map((item) =>
        typeof item === "object" ? JSON.stringify(item) : String(item)
      );
    case "map":
      return JSON.stringify(value, null, 2);
    case "boolean":
      return Boolean(value);
    case "integer":
    case "double":
      return value as number;
    default:
      return value === null || value === undefined ? "" : String(value);
  }
};

/** Convert an API config object into editable rows (sorted for stable display). */
export const entriesFromConfig = (config: ConfigMap): IConfigEntry[] =>
  Object.keys(config)
    .sort((a, b) => a.localeCompare(b))
    .map((key) => {
      const type = inferValueType(config[key]);
      return { rowId: nextRowId(), key, type, value: toEditorValue(config[key], type) };
    });

/** Best-effort conversion of a list token back to a primitive. */
const parseListToken = (token: string): unknown => {
  const trimmed = token.trim();
  if (trimmed === "true") return true;
  if (trimmed === "false") return false;
  if (trimmed !== "" && !Number.isNaN(Number(trimmed))) return Number(trimmed);
  try {
    const parsed = JSON.parse(trimmed);
    if (typeof parsed === "object" && parsed !== null) return parsed;
  } catch (_) {
    // Not JSON — keep as plain string.
  }
  return token;
};

export interface IEntriesValidationResult {
  config?: ConfigMap;
  error?: string;
}

/**
 * Validate rows and serialize them back into the API config object.
 * Returns an error message instead of a config when the rows are invalid.
 */
export const configFromEntries = (entries: IConfigEntry[]): IEntriesValidationResult => {
  const config: ConfigMap = {};
  for (const entry of entries) {
    const key = entry.key.trim();
    if (!key) {
      return { error: "Every config entry must have a key." };
    }
    if (key in config) {
      return { error: `Duplicate config key: ${key}` };
    }
    switch (entry.type) {
      case "boolean":
        config[key] = Boolean(entry.value);
        break;
      case "integer":
      case "double":
        config[key] =
          entry.value === null || entry.value === "" ? null : Number(entry.value);
        break;
      case "list":
        config[key] = ((entry.value as string[]) ?? []).map(parseListToken);
        break;
      case "map": {
        try {
          const parsed = JSON.parse((entry.value as string) || "{}");
          if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
            return { error: `"${key}" must be a JSON object.` };
          }
          config[key] = parsed;
        } catch (_) {
          return { error: `"${key}" contains invalid JSON.` };
        }
        break;
      }
      default:
        config[key] = entry.value === "" ? null : entry.value;
    }
  }
  return { config };
};
